import { all, call, fork, put, select, takeLatest } from "redux-saga/effects";
import UserService from "../../services/User";

import { Types, listUserSuccess, updateUserLoading } from "../reducers/User";

import {
  buttonLoading,
  toggleModal
} from "../actions/loading";
import { notificationMessage } from "../reducers/FeedbackMessage";

const DEFAULT_PAGINATION = {
  totalItems: 0,
  totalPages: 0,
  itemsPerPage: 10,
  currentPage: 1
};

const normalizeListResponse = (response, fallbackParams) => {
  if (response && Array.isArray(response.data)) {
    const { data, ...pagination } = response;
    return {
      users: data,
      pagination: {
        ...DEFAULT_PAGINATION,
        ...pagination,
        totalItems: pagination.totalItems || pagination.total || DEFAULT_PAGINATION.totalItems,
        itemsPerPage: pagination.itemsPerPage || pagination.pageSize || fallbackParams.limit || DEFAULT_PAGINATION.itemsPerPage,
        currentPage: pagination.currentPage || pagination.page || fallbackParams.page || DEFAULT_PAGINATION.currentPage
      }
    };
  }

  if (Array.isArray(response)) {
    return {
      users: response,
      pagination: {
        ...DEFAULT_PAGINATION,
        itemsPerPage: fallbackParams.limit || DEFAULT_PAGINATION.itemsPerPage,
        currentPage: fallbackParams.page || DEFAULT_PAGINATION.currentPage
      }
    };
  }

  return {
    users: [],
    pagination: {
      ...DEFAULT_PAGINATION,
      itemsPerPage: fallbackParams.limit || DEFAULT_PAGINATION.itemsPerPage,
      currentPage: fallbackParams.page || DEFAULT_PAGINATION.currentPage
    }
  };
};

const getListParams = state => {
  const companyId = state.filter.companyId;
  return {
    ...state.user.listParams,
    ...companyId && { companyIds: companyId }
  };
};

const listRequest = async params =>
  UserService.list(params)
    .then(users => users)
    .catch(error => {
      throw error;
    });

const createRequest = async data =>
  UserService.create(data)
    .then(user => user)
    .catch(error => {
      throw error;
    });

const removeRequest = async id =>
  UserService.remove(id)
    .then(removedUser => removedUser)
    .catch(error => {
      throw error;
    });

const updateRequest = async data =>
  UserService.update(data)
    .then(updatedUser => updatedUser)
    .catch(error => {
      throw error;
    });

function* list() {
  try {
    yield put(updateUserLoading(true));

    const params = yield select(getListParams);
    const response = yield call(listRequest, params);
    const { users, pagination } = normalizeListResponse(response, params);

    yield put(listUserSuccess(users, pagination));
  } catch (error) {
    yield put(notificationMessage("notifications.user.list.error", "error"));
  } finally {
    yield put(updateUserLoading(false));
  }
}

function* create({ payload }) {
  try {
    yield put(buttonLoading(true));

    yield call(createRequest, payload);
    yield put(buttonLoading(false));
    yield put(toggleModal(false));

    const params = yield select(getListParams);
    const response = yield call(listRequest, params);
    const { users, pagination } = normalizeListResponse(response, params);
    yield put(listUserSuccess(users, pagination));
    yield put(
      notificationMessage("notifications.user.create.success", "success")
    );
  } catch (error) {
    yield put(buttonLoading(false));
    yield put(toggleModal(false));

    yield put(notificationMessage("notifications.user.create.error", "error"));
  }
}

function* remove({ payload }) {
  try {
    yield put(buttonLoading(true));

    yield call(removeRequest, payload);
    yield put(buttonLoading(false));

    const params = yield select(getListParams);
    const response = yield call(listRequest, params);
    const { users, pagination } = normalizeListResponse(response, params);
    yield put(listUserSuccess(users, pagination));
    yield put(
      notificationMessage("notifications.user.delete.success", "success")
    );
  } catch (error) {
    yield put(buttonLoading(false));
    yield put(notificationMessage("notifications.user.delete.error", "error"));
  }
}

function* update({ payload }) {
  try {
    yield put(buttonLoading(true));

    yield call(updateRequest, payload);
    yield put(buttonLoading(false));
    yield put(toggleModal(false));

    const params = yield select(getListParams);
    const response = yield call(listRequest, params);
    const { users, pagination } = normalizeListResponse(response, params);
    yield put(listUserSuccess(users, pagination));
  } catch (error) {
    yield put(buttonLoading(false));
    yield put(toggleModal(false));

    yield put(notificationMessage("notifications.user.update.error", "error"));
  }
}

export function* removeSaga() {
  yield takeLatest(Types.REMOVE_USER, remove);
}

export function* listSaga() {
  yield takeLatest(Types.LIST_USER, list);
}

export function* createSaga() {
  yield takeLatest(Types.CREATE_USER, create);
}

export function* updateSaga() {
  yield takeLatest(Types.UPDATE_USER, update);
}

export default function* rootSaga() {
  yield all([
    fork(listSaga),
    fork(createSaga),
    fork(updateSaga),
    fork(removeSaga)
  ]);
}
